import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress,
  Pagination,
  TextField,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { fetchReport, handleError, formatCurrency } from '../../utils/lib';
import { useSnackbar } from '../../providers/SnackBarProvider';
import { useNavigate } from 'react-router-dom';
import useNavigationHelperStore from '../../store/navigationHelperStore';
import { useTranslation } from 'react-i18next';

const ProductsBelowMinimumReport = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const { setNavigation, state } = useNavigationHelperStore();
  
  // Load saved search parameters when returning to the page
  useEffect(() => {
    if (state) {
      const { page: savedPage, data: savedData } = state;
      if (savedPage) setPage(savedPage);
      if (savedData) setData(savedData);
      
      if (savedPage) {
        fetchProducts(savedPage);
      } else {
        fetchProducts(1);
      }
    } else {
      fetchProducts(1);
    }
  }, []);

  // Save search parameters when leaving this page
  const saveCurrentState = () => {
    setNavigation('/main/reports', {
      page,
      data
    });
  };

  const fetchProducts = async (pageNumber) => {
    setIsLoading(true);
    try {
      const response = await fetchReport('PRODUCTS_BELOW_MINIMUN', pageNumber);
      setData(response);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handlePageChange = (event, value) => {
    setPage(value);
    fetchProducts(value);
  };

  const handleProductClick = (product) => {
    // Save current state before navigating
    saveCurrentState();
    navigate('/main/view-product', { state: { id: product.id } });
  };

  const getStockStatusChip = (product) => {
    if (product.quantity <= 0) {
      return <Chip label={t('reports.noStock')} color="error" size="small" />;
    }
    
    return <Chip label={t('reports.lowStock')} color="warning" size="small" />;
  };

  const getRequiredQuantity = (product) => {
    return Math.max(0, product.min_quantity - product.quantity);
  };

  const getProductCategories = (categories) => {
    if (!categories || categories.length === 0) return t('reports.noCategory');
    return categories.map(cat => cat.name).join(', ');
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {data ? (
        <>
          {data.content && data.content.length > 0 ? (
            <>
              <Box mb={2}>
                <Typography variant="h6">
                  {t('reports.productsBelowMinTitle')} ({data.total_results || data.content.length})
                </Typography>
              </Box>
              
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table sx={{ minWidth: 350 }} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('reports.productLabel')}</TableCell>
                      <TableCell>{t('common.category')}</TableCell>
                      <TableCell>{t('reports.currentStock')}</TableCell>
                      <TableCell>{t('reports.minStock')}</TableCell>
                      <TableCell>{t('common.status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.content.map((product) => (
                      <TableRow 
                        key={product.id}
                        hover
                        onClick={() => handleProductClick(product)}
                        sx={{ '&:hover': { cursor: 'pointer' } }}
                      >
                        <TableCell>{product.name}</TableCell>
                        <TableCell>{getProductCategories(product.categories)}</TableCell>
                        <TableCell>{product.quantity || 0}{product.unit ? ` ${product.unit.name}` : ''}</TableCell>
                        <TableCell>{product.min_quantity || 0}{product.unit ? ` ${product.unit.name}` : ''}</TableCell>
                        <TableCell>{getStockStatusChip(product)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {data.total_pages && data.total_pages > 1 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={data.total_pages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          ) : (
            <Box textAlign="center" p={3}>
              <Typography variant="body1">{t('reports.noProductsBelowMin')}</Typography>
            </Box>
          )}
        </>
      ) : (
        <Box textAlign="center" p={3}>
          <Typography variant="body1">{t('reports.noProductsBelowMinToShow')}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default ProductsBelowMinimumReport;